import { useEffect } from 'react';
import { preserveGlobalFields } from '../../bidAnalysisTemplate.logic';

export interface UseMASDirectEffectsDeps {
  BidAnalysisStore: any;
  gsmsHndlgType: string;
  activePackSizeTab: number | null;
  activeYearTab: number | null;
  packSizeFormData: Record<number, any>;
  yearFormData: Record<number, Record<number, any>>;
  formData: any;
  recalculateFields: (data: any, changedField?: string) => any;
  setPackSizeFormData: (updater: (prev: Record<number, any>) => Record<number, any>) => void;
  setFormData: (updater: ((prev: any) => any) | any) => void;
  setYearFormData: (updater: (prev: Record<number, Record<number, any>>) => Record<number, Record<number, any>>) => void;
}

export function useMASDirectEffects(deps: UseMASDirectEffectsDeps): void {
  const {
    BidAnalysisStore,
    gsmsHndlgType,
    activePackSizeTab,
    activeYearTab,
    packSizeFormData,
    yearFormData,
    formData,
    recalculateFields,
    setPackSizeFormData,
    setFormData,
    setYearFormData,
  } = deps;

  // ── Recalculate on gsmsHndlgType change for Direct products ───────────
  useEffect(() => {
    const bidDetails = BidAnalysisStore.currentBidDetails;
    const currentProductType = bidDetails?.product_type;
    if (gsmsHndlgType && currentProductType === 'direct' && activePackSizeTab !== null) {
      const currentPackData = packSizeFormData[activePackSizeTab] || formData;

      if (currentPackData.gsmsHndlgType !== gsmsHndlgType) {
        const updatedData = {
          ...formData,
          ...currentPackData,
          gsmsHndlgType,
        };

        const recalculatedData = recalculateFields(updatedData, 'gsmsHndlgType');

        setPackSizeFormData(prev => {
          const existing = prev[activePackSizeTab] || {};
          return {
            ...prev,
            [activePackSizeTab]: {
              ...existing,
              ...recalculatedData,
              gsmsHndlgType,
            },
          };
        });

        setFormData((prev: any) => ({
          ...prev,
          ...recalculatedData,
          ...preserveGlobalFields(recalculatedData, prev),
          gsmsHndlgType,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gsmsHndlgType, BidAnalysisStore.currentBidDetails?.product_type, activePackSizeTab]);

  // ── Populate GSMS Bottle Capacity (Direct products) ───────────────────
  useEffect(() => {
    const bidDetails = BidAnalysisStore.currentBidDetails;
    const templateType = bidDetails?.template_type || 'mas';
    const productType = bidDetails?.product_type;
    const isNC = templateType === 'nc';
    const isDirect = productType === 'direct';
    if (!isDirect && !isNC) return;
    if (!gsmsHndlgType) return;
    if (activePackSizeTab === null) return;

    const currentPackData =
      isNC && activeYearTab !== null && activeYearTab !== 5
        ? yearFormData[activePackSizeTab]?.[activeYearTab] || {}
        : packSizeFormData[activePackSizeTab] || {};
    const packSizeVal =
      parseFloat(currentPackData.packSize) ||
      parseFloat(currentPackData.gsmsPackSize) ||
      parseFloat(formData.packSize) ||
      parseFloat(formData.gsmsPackSize);
    if (!packSizeVal || packSizeVal <= 0) return;

    const currentBottleCapacity = currentPackData.gsmsBottleCapacity ?? formData.gsmsBottleCapacity;
    const dataGsmsHndlgType = currentPackData.gsmsHndlgType ?? formData.gsmsHndlgType;
    const handlingTypeChanged = dataGsmsHndlgType !== gsmsHndlgType;
    if (
      !handlingTypeChanged &&
      currentBottleCapacity !== null &&
      currentBottleCapacity !== undefined &&
      String(currentBottleCapacity).trim() !== ''
    )
      return;

    let currentYearTargetLaunchDate: string | Date | null | undefined = formData.targetLaunchDate;
    if (isNC && activeYearTab !== null && activeYearTab !== 5 && activeYearTab > 0) {
      const baseDate = formData.targetLaunchDate;
      if (baseDate) {
        try {
          const base = typeof baseDate === 'string' ? new Date(baseDate) : baseDate;
          if (base instanceof Date && !isNaN(base.getTime())) {
            const yearDate = new Date(base);
            yearDate.setFullYear(base.getFullYear() + activeYearTab);
            currentYearTargetLaunchDate = yearDate;
          }
        } catch {
          currentYearTargetLaunchDate = formData.targetLaunchDate;
        }
      }
    }

    const supplierDiscountFromPack = isNC
      ? (yearFormData[activePackSizeTab]?.[0]?.supplierDiscount ??
        currentPackData.supplierDiscount ??
        formData.supplierDiscount)
      : (packSizeFormData[activePackSizeTab]?.supplierDiscount ?? formData.supplierDiscount);
    const supplierCodeFromPack = isNC
      ? (yearFormData[activePackSizeTab]?.[0]?.supplierCode ??
        currentPackData.supplierCode ??
        formData.supplierCode)
      : (packSizeFormData[activePackSizeTab]?.supplierCode ?? formData.supplierCode);

    const updatedData = {
      ...formData,
      ...currentPackData,
      gsmsHndlgType,
      gsmsPackSize: String(packSizeVal),
      packSize: String(packSizeVal),
      supplierDiscount: supplierDiscountFromPack ?? formData.supplierDiscount ?? 0,
      supplierCode: supplierCodeFromPack ?? formData.supplierCode ?? '',
      ...(isNC && { gsmsPackSize: String(packSizeVal) }),
      ...(isNC && currentYearTargetLaunchDate && { targetLaunchDate: currentYearTargetLaunchDate }),
    };

    const recalculatedData = recalculateFields({ updatedData, changedField: 'gsmsHndlgType' });

    if (!recalculatedData.gsmsBottleCapacity) return;

    if (isNC && activeYearTab !== null && activeYearTab !== 5) {
      setYearFormData((prev: Record<number, Record<number, any>>) => ({
        ...prev,
        [activePackSizeTab]: {
          ...prev[activePackSizeTab],
          [activeYearTab]: {
            ...(prev[activePackSizeTab]?.[activeYearTab] || {}),
            ...recalculatedData,
            gsmsBottleCapacity: recalculatedData.gsmsBottleCapacity,
          },
        },
      }));
    } else {
      setPackSizeFormData((prev: Record<number, any>) => ({
        ...prev,
        [activePackSizeTab]: {
          ...(prev[activePackSizeTab] || {}),
          ...recalculatedData,
          gsmsBottleCapacity: recalculatedData.gsmsBottleCapacity,
        },
      }));
    }
    setFormData((prev: any) => ({
      ...prev,
      ...recalculatedData,
      gsmsBottleCapacity: recalculatedData.gsmsBottleCapacity,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    gsmsHndlgType,
    activePackSizeTab,
    activeYearTab,
    formData.gsmsBottleCapacity,
    formData.gsmsHndlgType,
    BidAnalysisStore.currentBidDetails?.template_type,
    BidAnalysisStore.currentBidDetails?.product_type,
  ]);
}
